import { useMemo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';
import { v4 as uuidv4 } from 'uuid';

import { NsTitle } from 'components/common/ns-title/NsTitle';
import { setInitialState as resetSelectedAddress } from 'state/selected-address/selected-address-slice';
import {
  setElectric,
  setGas,
  setGasElectric
} from 'state/fuel-type/fuel-type-slice';
import { getFuelValue } from './getFuelValue';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { ChangeAddress } from 'components/common/change-address/ChangeAddress';
import { NsForm } from 'components/common/ns-form/NsForm';
import { useMeterType } from 'hooks/useMeterType';
import { setValidationRequired } from 'state/meterpoint-validation/meterpoint-validation-slice';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { useNavigation } from 'hooks/useNavigation';
import { useHasInitialState } from 'components/direct-sales/personalise-quote/hooks/useHasInitialState';
import { setPersonaliseQuoteShowFuel } from 'state/settings/settings-slice';

import { FUEL_OPTIONS } from 'config/app-config';

export const testId = 'fuelTypeTestId';

const radioButtonOptions = [
  {
    label: 'Gas & electricity',
    value: FUEL_OPTIONS.DUAL
  },
  {
    label: 'Electricity only',
    value: FUEL_OPTIONS.ELECTRIC
  },
  {
    label: 'Gas only',
    value: FUEL_OPTIONS.GAS
  }
];

export const FuelType = () => {
  const dispatch = useDispatch();
  const { eventTypes, next } = useNavigation();
  const [key, setKey] = useState(uuidv4());

  const fuelType = useSelector(selectFuelType, shallowEqual);
  const fuelValue = useMemo(() => getFuelValue(fuelType), [fuelType]);
  const {
    hasElectricityMeter,
    hasGasMeter,
    isRelatedElectricityMeter
  } = useMeterType();
  const { fuel: hasFuelInitialState } = useHasInitialState();

  const handleChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;
      switch (value) {
        case FUEL_OPTIONS.ELECTRIC:
          dispatch(setElectric());
          dispatch(
            setValidationRequired({
              electricity: !hasElectricityMeter && !isRelatedElectricityMeter,
              gas: false
            })
          );

          break;
        case FUEL_OPTIONS.GAS:
          dispatch(setGas());
          dispatch(
            setValidationRequired({
              electricity: false,
              gas: !hasGasMeter
            })
          );
          break;
        default:
          dispatch(setGasElectric());
          dispatch(
            setValidationRequired({
              electricity: !hasElectricityMeter && !isRelatedElectricityMeter,
              gas: !hasGasMeter
            })
          );
      }
    },
    [dispatch, hasElectricityMeter, hasGasMeter, isRelatedElectricityMeter]
  );

  const handleGoNext = useCallback(() => {
    next({ eventType: eventTypes.NEXT });
  }, [next, eventTypes.NEXT]);

  const handleChangeAddress = useCallback(() => {
    next({ eventType: eventTypes.CHANGE_ADDRESS, wasVisited: true });
    dispatch(resetSelectedAddress());
  }, [next, eventTypes, dispatch]);

  useEffect(() => {
    if (hasFuelInitialState) {
      dispatch(setGasElectric());
      dispatch(
        setValidationRequired({
          electricity: !hasElectricityMeter && !isRelatedElectricityMeter,
          gas: !hasGasMeter
        })
      );
      dispatch(setPersonaliseQuoteShowFuel(false));
      setKey(uuidv4());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(
    () => (
      <NsPanel testId={testId}>
        <ChangeAddress handleGoBack={handleChangeAddress} />
        <NsContent>
          <NsTitle title="What type of quote would you like?​" />
        </NsContent>
        <NsForm onSubmit={handleGoNext}>
          <NsRadioInput
            key={key}
            name="fuelType"
            dataTestId="radioFuelTypeTestId"
            onChange={handleChange}
            value={fuelValue}
            options={radioButtonOptions}
          />
          <NsPrimaryCta>Continue</NsPrimaryCta>
        </NsForm>
      </NsPanel>
    ),
    [handleChange, handleGoNext, handleChangeAddress, fuelValue, key]
  );
};

export default FuelType;
